import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import {
  AnalysisHistoryDetailResponse,
  AnalysisHistoryListResponse,
  GetAnalysisHistoryParams
} from "@/types/analysisHistory";

import {
  ANALYSIS_HISTORY_PER_PAGE,
  deleteAnalysisHistory,
  getAnalysisHistoryApi,
  getAnalysisHistoryDetail
} from "./analysisHistoryApi";

export const useGetInfiniteAnalysisHistory = (
  params?: GetAnalysisHistoryParams,
  config?: UseInfiniteQueryOptions<
    AnalysisHistoryListResponse,
    Error,
    InfiniteData<AnalysisHistoryListResponse>
  >
): UseInfiniteQueryResult<InfiniteData<AnalysisHistoryListResponse>, Error> => {
  return useInfiniteQuery<
    AnalysisHistoryListResponse,
    Error,
    InfiniteData<AnalysisHistoryListResponse>
  >({
    queryKey: ["analysis-history-list-infinite", params],
    queryFn: ({ pageParam }) =>
      getAnalysisHistoryApi(pageParam as number, params),
    initialPageParam: 1,
    getNextPageParam: (lastPage: AnalysisHistoryListResponse, allPages) => {
      if (lastPage?.data?.length < ANALYSIS_HISTORY_PER_PAGE) return undefined;
      return allPages.length + 1;
    },
    retry: 1,
    staleTime: 0,
    retryDelay: 1000,
    ...config
  });
};

export const useGetAnalysisHistoryDetail = (
  id: string,
  options?: UseQueryOptions<
    AnalysisHistoryDetailResponse,
    Error,
    AnalysisHistoryDetailResponse
  >
) => {
  return useQuery({
    queryKey: ["analysis-history-detail", id],
    queryFn: () => getAnalysisHistoryDetail(id),
    enabled: !!id,
    ...options
  });
};

export const useDeleteAnalysisHistory = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      return deleteAnalysisHistory(id);
    }
  });
};
